import { api } from '../client/axiosClient';
import { CustomResponseEntity } from '../types/dtos/common';
import { InspectionsFindingDto } from '../types/dtos/inspection';
import { ENDPOINTS } from '../endpoints';

/**
 * InspectionFindingService handles CRUD operations for inspection findings.
 *
 * URL Pattern: /api/v1/inspections/{inspectionId}/findings
 * Organization context is provided via X-Organization-Id header (injected by axiosClient).
 *
 * Migration: The organizationId parameter has been removed from URL paths. The backend
 * extracts organization context from the X-Organization-Id header for tenant scoping.
 *
 * @deprecated organizationId parameter is deprecated and no longer used.
 */
class InspectionFindingService {
  /**
   * Get all findings for an inspection.
   * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param inspectionId - Inspection UUID
   * @param isActive - Filter by active status
   */
  async findAll(
    _organizationId: string,
    inspectionId: string,
    isActive?: boolean
  ): Promise<CustomResponseEntity<InspectionsFindingDto[]>> {
    return api.get(ENDPOINTS.findings.base(_organizationId, inspectionId), {
      params: { isActive },
    });
  }

  /**
   * Get a finding by ID.
   * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param inspectionId - Inspection UUID
   * @param id - Finding UUID
   */
  async findById(
    _organizationId: string,
    inspectionId: string,
    id: string
  ): Promise<CustomResponseEntity<InspectionsFindingDto>> {
    return api.get(ENDPOINTS.findings.byId(_organizationId, inspectionId, id));
  }

  /**
   * Create a new finding.
   * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param inspectionId - Inspection UUID
   * @param data - Finding data
   */
  async create(
    _organizationId: string,
    inspectionId: string,
    data: InspectionsFindingDto
  ): Promise<CustomResponseEntity<InspectionsFindingDto>> {
    return api.post(ENDPOINTS.findings.base(_organizationId, inspectionId), data);
  }

  /**
   * Update a finding.
   * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param inspectionId - Inspection UUID
   * @param id - Finding UUID
   * @param data - Updated finding data
   */
  async update(
    _organizationId: string,
    inspectionId: string,
    id: string,
    data: InspectionsFindingDto
  ): Promise<CustomResponseEntity<InspectionsFindingDto>> {
    return api.put(
      ENDPOINTS.findings.byId(_organizationId, inspectionId, id),
      data
    );
  }

  /**
   * Delete a finding.
   * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param inspectionId - Inspection UUID
   * @param id - Finding UUID
   */
  async delete(
    _organizationId: string,
    inspectionId: string,
    id: string
  ): Promise<CustomResponseEntity<void>> {
    return api.delete(ENDPOINTS.findings.byId(_organizationId, inspectionId, id));
  }
}

export const inspectionFindingService = new InspectionFindingService(); 